import {skipToken, useInfiniteQuery, useMutation, useQuery} from '@tanstack/react-query';
import {
    ConvertFiltersInput,
    ConvertNameToEntitiesInput,
    FilterAiColumn,
    FilterColumnV2,
    FilterQueryQuery,
    GetEntitiesByIdInput,
    GlobalMediaValueColumnFilter,
    GlobalMediaValueInputColumn,
    GlobalMediaValueRestrictedtemDisplayType
} from '~/graphql-react-query/graphql';
import useClientMainAuthorizedFetch from '../clientMainAuthorisedFetch';
import {
    ConvertNamesToEntities,
    FilterQuery,
    GetEntitiesByIdQuery,
    ConvertNameFiltersToIds,
    CreativeAiFilters,
    MediaValueAiFilters,
    GlobalMvFilterQuery
} from './graphqlQueries';
import {AdType, Brand, Category, CreativeStatus, Industry} from 'bigdatr-style';
import {MediaOwner, MediaType, Product, Publication, Publisher, Region} from 'bigdatr-style';
import AdvertiserDomain from '~/feature/AdvertiserDomain';
import AdvertiserName from '~/feature/AdvertiserName';
import Changeset from '~/feature/Changeset';
import Segment from '~/segment/data/Segment';
import PlacementType from '~/feature/PlacementType';
import Location from '~/feature/Location';
import {TFilter} from '~/feature/filter/FilterCheckbox';
import {useCountry} from '~/feature/country/CountryContext';
import {toGmvCountry} from '~/feature/media-value/helpers';

export type FilterForFilteringFilters = {
    brand?: string[];
    category?: string[];
    product?: string[];
    mediaOwner?: string[];
    mediaType?: string[];
    industry?: string[];
    publisher?: string[];
    aiAdvertiserV1?: string[];
    aiCategoryV1?: string[];
    aiIndustryV1?: string[];
    aiProductV1?: string[];
};

export type AIFilters = {
    aiAdvertiserV1?: string[];
    aiCategoryV1?: string[];
    aiIndustryV1?: string[];
    aiProductV1?: string[];
};

export function useFilters(
    args: {
        input: {country: string; searchQuery: string; column: FilterColumnV2; pageSize?: number};
        filter?: FilterForFilteringFilters;
        type: 'creative' | 'mediaValue';
    },
    options?: {enabled: boolean}
) {
    const {column, country, searchQuery, pageSize = 100} = args.input;
    const {filter, type} = args;
    const request = useClientMainAuthorizedFetch();
    const enabled = options?.enabled ?? true;
    return useInfiniteQuery({
        queryKey: ['filter', column, searchQuery, country, type, filter, pageSize],
        queryFn: enabled
            ? async ({pageParam}) => {
                  const response = await request(FilterQuery, {
                      input: {
                          column,
                          searchQuery: searchQuery.trim(),
                          country,
                          pageSize: pageSize,
                          pageNum: pageParam
                      },
                      filter: filter,
                      options: {
                          limitResponseByAccess: false,
                          type
                      }
                  });
                  const filters = response.filterV2?.filters;
                  if (!filters) throw new Error('Filters not returned from api');

                  const data = mapResponseToClassInstances(filters.items);
                  const hasNextPage = filters.pageInfo.hasNextPage;
                  const pageNum = filters.pageInfo.currentPage;
                  return {data, pageNum, hasNextPage};
              }
            : skipToken,
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            if (lastPage.hasNextPage) return lastPage.pageNum + 1;
            return null;
        }
    });
}

export function useCreativeAiFilters(
    args: {
        input: {
            country: string;
            searchQuery: string;
            column: FilterAiColumn;
            includeSubscribedIndustriesInRequests?: boolean;
        };
        filter?: AIFilters;
    },
    options?: {enabled: boolean}
) {
    const {column, country, searchQuery, includeSubscribedIndustriesInRequests = true} = args.input;
    const {filter} = args;
    const request = useClientMainAuthorizedFetch();
    const enabled = options?.enabled ?? true;

    return useInfiniteQuery({
        queryKey: [
            'creativeAiFilters',
            column,
            searchQuery,
            country,
            filter,
            includeSubscribedIndustriesInRequests
        ],
        queryFn: enabled
            ? async ({pageParam}) => {
                  const response = await request(CreativeAiFilters, {
                      input: {
                          column,
                          searchQuery: searchQuery.trim(),
                          country,
                          pageSize: 100,
                          offset: pageParam,
                          includeSubscribedIndustriesInRequests
                      },
                      filter: filter
                  });

                  const filterAiEntities = response.aiEntities?.filterAiEntitiesV2;
                  if (!filterAiEntities) throw new Error('Filters not returned from api');

                  return {
                      data: filterAiEntities.items,
                      pageInfo: filterAiEntities.pageInfo,
                      column
                  };
              }
            : skipToken,
        initialPageParam: 0,
        getNextPageParam: (lastPage) => {
            const {hasNextPage, nextOffset} = lastPage.pageInfo ?? {};
            if (hasNextPage) return nextOffset;
        }
    });
}

export function useMediaValueAiFilters(
    args: {
        input: {
            country: string;
            searchQuery: string;
            column: FilterAiColumn;
            includeSubscribedIndustriesInRequests?: boolean;
        };
        filter?: AIFilters;
    },
    options?: {enabled?: boolean}
) {
    const {column, country, searchQuery, includeSubscribedIndustriesInRequests = true} = args.input;
    const {filter} = args;
    const request = useClientMainAuthorizedFetch();
    const enabled = options?.enabled ?? true;

    return useInfiniteQuery({
        queryKey: [
            'mediaValueAiFilters',
            column,
            searchQuery,
            country,
            filter,
            includeSubscribedIndustriesInRequests
        ],
        queryFn: enabled
            ? async ({pageParam}) => {
                  const response = await request(MediaValueAiFilters, {
                      input: {
                          column,
                          searchQuery: searchQuery.trim(),
                          country,
                          pageSize: 100,
                          offset: pageParam,
                          includeSubscribedIndustriesInRequests
                      },
                      filter: filter
                  });

                  const filterAiEntities = response.mediaValueEntities?.filterMediaValueEntities;
                  if (!filterAiEntities) throw new Error('Filters not returned from api');

                  return {
                      data: filterAiEntities.items,
                      pageInfo: filterAiEntities.pageInfo,
                      column
                  };
              }
            : skipToken,
        initialPageParam: 0,
        getNextPageParam: (lastPage) => {
            const {hasNextPage, nextOffset} = lastPage.pageInfo ?? {};
            if (hasNextPage) return nextOffset;
        }
    });
}

export function useApiFetchFilterLabels(
    input: GetEntitiesByIdInput,
    options?: {enabled?: boolean}
) {
    const request = useClientMainAuthorizedFetch();
    const enabled = options?.enabled ?? true;
    return useQuery({
        queryKey: ['filterLabels', input],
        queryFn: enabled
            ? async () => {
                  const response = await request(GetEntitiesByIdQuery, {input});
                  if (!response.filterV2?.getEntitiesById)
                      throw new Error('Entites by id not returned from api');

                  const data = response.filterV2.getEntitiesById;
                  const {
                      advertiserDomain,
                      advertiserName,
                      location,
                      placementType,
                      status,
                      ...responseAsClassInstances
                  } = mapResponseToClassInstances(data);

                  return responseAsClassInstances;
              }
            : skipToken
    });
}

export function useFetchFiltersByName() {
    const request = useClientMainAuthorizedFetch();
    return useMutation({
        mutationFn: async ({type, payload}: {type: 'brand' | 'industry'; payload: string[]}) => {
            const response = await request(ConvertNamesToEntities, {input: {[type]: payload}});
            const data = response.filterV2?.convertNamesToEntities;
            if (!data) throw new Error('Data not returned from api');
            const {brand, industry} = mapResponseToClassInstances(data);
            return {brand, industry} as Record<string, TFilter[]>;
        }
    });
}

export type NamesToEntitiesType =
    | 'adType'
    | 'brand'
    | 'industry'
    | 'mediaType'
    | 'region'
    | 'publisher'
    | 'publication'
    | 'mediaOwner'
    | 'mediaTypeSynonym';
export function useFetchEntitiesByName() {
    const request = useClientMainAuthorizedFetch();
    return useMutation({
        mutationFn: async (input: ConvertNameToEntitiesInput) => {
            const response = await request(ConvertNamesToEntities, {input});
            const data = response.filterV2?.convertNamesToEntities;
            if (!data) throw new Error('Data not returned from api');
            const instancedData = mapResponseToClassInstances(data);
            return instancedData;
        }
    });
}

export function useConvertNameFiltersToIds(
    input: ConvertFiltersInput,
    options?: {enabled?: boolean}
) {
    const request = useClientMainAuthorizedFetch();
    const enabled = options?.enabled ?? true;
    return useQuery({
        queryKey: ['convertNameFiltersToIds', input],
        queryFn: enabled
            ? async () => {
                  const response = await request(ConvertNameFiltersToIds, {input});
                  const rawColumns = response.filterV2?.convertNameFiltersToIds;
                  if (!rawColumns) throw new Error('Convert Names to ids not returned from api');
                  return {convertNameFiltersToIds: rawColumns};
              }
            : skipToken
    });
}

export function useGlobalMvFilters(
    input: {
        searchText: string;
        column: GlobalMediaValueInputColumn;
        restrictedItemDisplayType: GlobalMediaValueRestrictedtemDisplayType;
        filter?: GlobalMediaValueColumnFilter;
    },
    options?: {enabled?: boolean}
) {
    const request = useClientMainAuthorizedFetch();
    const {country} = useCountry();
    const graphqlCountry = toGmvCountry(country);
    const {searchText, column, restrictedItemDisplayType, filter} = input;
    const enabled = options?.enabled ?? true;
    const pageSize = 100;

    return useInfiniteQuery({
        queryKey: [
            'globalMvFilters',
            graphqlCountry,
            column,
            searchText,
            restrictedItemDisplayType,
            filter,
            pageSize
        ],
        queryFn: enabled
            ? async ({pageParam}) => {
                  const response = await request(GlobalMvFilterQuery, {
                      column,
                      country: graphqlCountry,
                      searchText,
                      restrictedItemDisplayType,
                      filter,
                      size: pageSize,
                      ...(pageParam && {after: pageParam})
                  });
                  const data = response.globalMediaValue.filterQuery;
                  if (!data) throw new Error('GMV filter data not returned from api');
                  return data;
              }
            : skipToken,
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) => {
            const {after, items} = lastPage;
            // we can safely assume there is no next page if the returned data is smaller than the page size
            if (items.length === pageSize && after) return after;
            return null;
        }
    });
}

//
// helpers
//

export type FilterEntities = {
    adType: AdType[];
    brand: Brand[];
    category: Category[];
    industry: Industry[];
    mediaOwner: MediaOwner[];
    mediaType: MediaType[];
    product: Product[];
    publication: Publication[];
    publisher: Publisher[];
    region: Region[];
    changeset: Changeset[];
    segment: Segment[];
};

type Input = NonNullable<NonNullable<FilterQueryQuery['filterV2']>['filters']>['items'];
type OtherInput = {
    mediaTypeSynonym?: {id: string; name: string; canonicalName: string}[] | null;
    adType?: {id: string; name: string; canonicalName: string}[] | null;
    advertiserName?: {id: string; name: string}[] | null;
    brand?: {id: string; name: string}[] | null;
    category?: {id: string; name: string}[] | null;
    industry?: {id: string; name: string}[] | null;
    location?: {id: string; name: string}[] | null;
    product?: {id: string; name: string}[] | null;
};

function mapResponseToClassInstances(props: Input & OtherInput) {
    return {
        adType: (props.adType || []).map((e) => new AdType(e)),
        advertiserDomain: (props.advertiserDomain || []).map((e) => new AdvertiserDomain(e)),
        advertiserName: (props.advertiserName || []).map((e) => new AdvertiserName(e)),
        brand: (props.brand || []).map((e) => new Brand(e)),
        category: (props.category || []).map((e) => new Category(e)),
        changeset: (props.changeset || []).map((e) => new Changeset(e)),
        industry: (props.industry || []).map((e) => new Industry(e)),
        location: (props.location || []).map((e) => new Location(e)),
        mediaOwner: (props.mediaOwner || []).map((e) => new MediaOwner(e)),
        mediaType: (props.mediaType || []).map((e) => new MediaType(e)),
        mediaTypeSynonym: (props.mediaTypeSynonym || []).map((e) => new MediaType(e)),
        placementType: (props.placementType || []).map((e) => new PlacementType(e)),
        product: (props.product || []).map((e) => new Product(e)),
        publication: (props.publication || []).map((e) => new Publication(e)),
        publisher: (props.publisher || []).map((e) => new Publisher(e)),
        region: (props.region || []).map((e) => new Region(e)),
        segment: (props.segment || []).map((e) => new Segment(e)),
        status: (props.status || []).map((e) => new CreativeStatus(e))
    };
}
